import { Button, Select, Stack, Text } from "@mantine/core";
import { IconLanguage } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { TEXT_MODES, SINGLE_MODEL_OPTIONS } from "@models/translation";
import { TargetLanguageSelect } from "@features/LanguageSelect";

interface Props {
  targets: string[];
  onTargetsChange: (value: string[]) => void;
  mode: string;
  onModeChange: (value: string) => void;
  model: string;
  onModelChange: (value: string) => void;
  canSubmit: boolean;
  submitting: boolean;
  onSubmit: () => void;
  disabled?: boolean;
}

const modeData = TEXT_MODES.map((m) => ({ value: m.value, label: m.label }));
const modelData = SINGLE_MODEL_OPTIONS.map((m) => ({ value: m.value, label: m.label }));
const modeHint = (value: string): string =>
  TEXT_MODES.find((m) => m.value === value)?.description ?? "";

export const TargetConfigPanel: React.FC<Props> = ({
  targets,
  onTargetsChange,
  mode,
  onModeChange,
  model,
  onModelChange,
  canSubmit,
  submitting,
  onSubmit,
  disabled,
}) => {
  const { t } = useTranslation();

  return (
    <Stack gap="sm" h="100%" style={{ flex: 1 }}>
      <Text fw={700} size="xs" tt="uppercase" style={{ letterSpacing: "0.06em" }} c="dimmed">
        {t("translation.targets")}
      </Text>

      <TargetLanguageSelect
        value={targets}
        onChange={onTargetsChange}
        placeholder={t("translation.targetPlaceholder")}
        disabled={disabled}
      />

      <Text fw={700} size="xs" mt="sm">
        {t("translation.mode")}
      </Text>
      <Text size="xs" c="dimmed" mt={-6} mb={2}>
        {modeHint(mode)}
      </Text>
      <Select
        data={modeData}
        value={mode}
        onChange={(v) => v && onModeChange(v)}
        allowDeselect={false}
        comboboxProps={{ withinPortal: true }}
        disabled={disabled}
        size="sm"
        radius="md"
        styles={{
          input: { fontWeight: 600, background: "var(--mantine-color-gray-0)" },
        }}
      />

      <Text fw={700} size="xs" mt="sm">
        {t("translation.model")}
      </Text>
      <Select
        data={modelData}
        value={model}
        onChange={(v) => v && onModelChange(v)}
        allowDeselect={false}
        comboboxProps={{ withinPortal: true }}
        disabled={disabled}
        size="sm"
        radius="md"
        styles={{
          input: { fontWeight: 600, background: "var(--mantine-color-gray-0)" },
        }}
      />

      <Button
        mt="auto"
        size="lg"
        leftSection={<IconLanguage size={18} />}
        onClick={onSubmit}
        disabled={!canSubmit}
        loading={submitting}
        fullWidth
        variant="filled"
        color="brand"
        radius="md"
        styles={{
          root: {
            fontWeight: 700,
            fontSize: "15px",
            boxShadow: canSubmit
              ? "0 6px 16px color-mix(in srgb, var(--mantine-color-brand-7) 28%, transparent)"
              : "none",
          },
        }}
      >
        {t("translation.translate")}
      </Button>
    </Stack>
  );
};
